import {
  adminCreateSite,
  adminDeleteSite,
  adminUpdateSite,
} from "@/api/api";
import { adminSitesQuery } from "@/api/queries";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { siteMethodLabel } from "@/lib/adminLabels";
import { siteInputSchema, type SiteInput } from "@/schemas/site";
import type { Site, SiteMethod } from "@/types/admin";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";

export const Route = createFileRoute("/admin/sites")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(adminSitesQuery()),
  component: AdminSitesPage,
});

const METHODS: SiteMethod[] = [
  "web_form",
  "email",
  "postal",
  "bankid_required",
];

function errorMessage(err: unknown, fallback: string): string {
  if (
    typeof err === "object" &&
    err !== null &&
    "response" in err &&
    typeof (err as { response?: unknown }).response === "object"
  ) {
    const data = (err as { response?: { data?: { message?: unknown } } })
      .response?.data;
    if (data && typeof data.message === "string") return data.message;
  }
  return fallback;
}

function AdminSitesPage() {
  const queryClient = useQueryClient();
  const { data: sites, isError } = useQuery(adminSitesQuery());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Site | null>(null);

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["admin", "sites"] });

  const toggleActive = useMutation({
    mutationFn: (site: Site) =>
      adminUpdateSite(site.id, { active: !site.active }),
    onSuccess: invalidate,
    onError: (err) => toast.error(errorMessage(err, "Kunde inte uppdatera.")),
  });

  const remove = useMutation({
    mutationFn: (site: Site) => adminDeleteSite(site.id),
    onSuccess: () => {
      toast.success("Webbplats borttagen.");
      invalidate();
    },
    onError: (err) =>
      toast.error(errorMessage(err, "Kunde inte ta bort webbplatsen.")),
  });

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (site: Site) => {
    setEditing(site);
    setDialogOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Webbplatser som raderingsbegäranden skapas för.
        </p>
        <Button onClick={openCreate} variant="cta">
          <Plus className="size-4" />
          Lägg till
        </Button>
      </div>

      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader className="bg-muted">
              <TableRow>
                <TableHead>Ordning</TableHead>
                <TableHead>Namn</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Metod</TableHead>
                <TableHead>Aktiv</TableHead>
                <TableHead className="text-right">Åtgärder</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isError ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-12 text-center text-red-600">
                    Kunde inte hämta webbplatser.
                  </TableCell>
                </TableRow>
              ) : !sites ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-12 text-center">
                    <Loader2 className="mx-auto size-6 animate-spin text-muted-foreground" />
                  </TableCell>
                </TableRow>
              ) : sites.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="py-12 text-center text-muted-foreground"
                  >
                    Inga webbplatser ännu.
                  </TableCell>
                </TableRow>
              ) : (
                sites.map((site) => (
                  <TableRow key={site.id}>
                    <TableCell className="text-muted-foreground">
                      {site.displayOrder}
                    </TableCell>
                    <TableCell className="font-medium">{site.name}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {site.slug}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {siteMethodLabel[site.method]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => toggleActive.mutate(site)}
                        className="cursor-pointer"
                      >
                        {site.active ? (
                          <Badge className="bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-300 dark:border-green-800">
                            Aktiv
                          </Badge>
                        ) : (
                          <Badge
                            variant="outline"
                            className="text-muted-foreground"
                          >
                            Inaktiv
                          </Badge>
                        )}
                      </button>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEdit(site)}
                          aria-label="Redigera"
                        >
                          <Pencil className="size-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            if (confirm(`Ta bort ${site.name}?`))
                              remove.mutate(site);
                          }}
                          aria-label="Ta bort"
                        >
                          <Trash2 className="size-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <SiteDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        onSaved={invalidate}
      />
    </div>
  );
}

function SiteDialog({
  open,
  onOpenChange,
  editing,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editing: Site | null;
  onSaved: () => void;
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<SiteInput>({
    resolver: zodResolver(siteInputSchema),
    defaultValues: { method: "web_form", active: true, displayOrder: 0 },
  });

  useEffect(() => {
    if (open) {
      reset({
        name: editing?.name ?? "",
        slug: editing?.slug ?? "",
        method: editing?.method ?? "web_form",
        instructions: editing?.instructions ?? "",
        displayOrder: editing?.displayOrder ?? 0,
        active: editing?.active ?? true,
      });
    }
  }, [open, editing, reset]);

  const onSubmit = async (data: SiteInput) => {
    const payload = { ...data, instructions: data.instructions || undefined };
    try {
      if (editing) {
        await adminUpdateSite(editing.id, payload);
        toast.success("Webbplats uppdaterad.");
      } else {
        await adminCreateSite(payload);
        toast.success("Webbplats skapad.");
      }
      onOpenChange(false);
      onSaved();
    } catch (err) {
      toast.error(errorMessage(err, "Kunde inte spara webbplatsen."));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {editing ? "Redigera webbplats" : "Ny webbplats"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="site-name">Namn</Label>
            <Input id="site-name" {...register("name")} />
            {errors.name && (
              <p className="text-xs text-red-600">{errors.name.message}</p>
            )}
          </div>
          <div className="space-y-1">
            <Label htmlFor="site-slug">Slug</Label>
            <Input id="site-slug" placeholder="t.ex. ratsit" {...register("slug")} />
            {errors.slug && (
              <p className="text-xs text-red-600">{errors.slug.message}</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="site-method">Metod</Label>
              <select
                id="site-method"
                {...register("method")}
                className="h-9 w-full rounded-md border bg-background px-2 text-sm"
              >
                {METHODS.map((m) => (
                  <option key={m} value={m}>
                    {siteMethodLabel[m]}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="site-order">Ordning</Label>
              <Input
                id="site-order"
                type="number"
                {...register("displayOrder")}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label htmlFor="site-instructions">Instruktioner (valfritt)</Label>
            <textarea
              id="site-instructions"
              rows={3}
              {...register("instructions")}
              className="w-full rounded-md border bg-background px-2 py-1.5 text-sm"
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" {...register("active")} />
            Aktiv
          </label>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Avbryt
            </Button>
            <Button type="submit" disabled={isSubmitting} variant="cta">
              {isSubmitting && <Loader2 className="size-4 animate-spin" />}
              Spara
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
